import {hpcAttrs} from '@github-ui/hpc'
import {testIdProps} from '@github-ui/test-id-props'

import styles from './SelfHostedRunners.module.css'

import {EmptyState} from './EmptyState'
import {SkeletonList} from './SkeletonList'
import {RunnerList} from './RunnerList'
import {RunnerListItem} from './RunnerListItem'
import {RunnersError} from './RunnersError'

import type {RunnersQueryState, SelfHostedRunnersData, SelfHostedRunnersResponse} from '../types'
import {useRunners} from '../hooks/use-runners'

export interface SelfHostedRunnersProps {
  fetchRunnersBasePath: string
  setUpRunnersLink?: string
  /** Caller-owned data. When omitted, the runners are fetched on mount. */
  state?: RunnersQueryState<SelfHostedRunnersData>
}

export function SelfHostedRunners({state, ...props}: SelfHostedRunnersProps) {
  return state ? <SelfHostedRunnersView {...props} state={state} /> : <FetchedSelfHostedRunners {...props} />
}

function FetchedSelfHostedRunners({fetchRunnersBasePath, setUpRunnersLink}: Omit<SelfHostedRunnersProps, 'state'>) {
  const repoSelfHosted = useRunners(`${fetchRunnersBasePath}/repository_self_hosted`)
  const shared = useRunners(`${fetchRunnersBasePath}/shared_runners`)
  const scaleSets = useRunners(`${fetchRunnersBasePath}/repository_scale_sets`)
  const all = [repoSelfHosted, shared, scaleSets]
  const loaded = !all.some(response => response.error || !response.runnersResponse)

  return (
    <SelfHostedRunnersView
      setUpRunnersLink={setUpRunnersLink}
      state={{
        data: loaded
          ? {
              repoSelfHosted: repoSelfHosted.runnersResponse as SelfHostedRunnersResponse,
              shared: shared.runnersResponse as SelfHostedRunnersResponse,
              scaleSets: scaleSets.runnersResponse as SelfHostedRunnersResponse,
            }
          : undefined,
        isLoading: all.some(response => response.isLoading),
        error: all.some(response => response.error),
        retry: () => {
          for (const response of all) response.retry()
        },
      }}
    />
  )
}

function SelfHostedRunnersView({
  state: {data, isLoading, error, retry},
  setUpRunnersLink,
}: Pick<SelfHostedRunnersProps, 'setUpRunnersLink'> & {state: RunnersQueryState<SelfHostedRunnersData>}) {
  if (isLoading) {
    return <SkeletonList listTitle="Self-hosted runners list" />
  }
  if (error || !data) {
    return <RunnersError onRetry={retry} />
  }

  const repoSelfHostedRunnersResponse = data.repoSelfHosted
  const {runners: repoSelfHostedRunners, total: repoSelfHostedRunnersCount} = repoSelfHostedRunnersResponse
  const {runners: sharedRunners, total: sharedRunnersCount} = data.shared
  const {runners: scaleSets, total: scaleSetsCount} = data.scaleSets
  const effectiveSetUpRunnersLink = setUpRunnersLink ?? repoSelfHostedRunnersResponse.setUpRunnersLink

  if (repoSelfHostedRunnersCount === 0 && sharedRunnersCount === 0 && scaleSetsCount === 0) {
    return <EmptyState selectedTab="self-hosted" setUpRunnersLink={effectiveSetUpRunnersLink} />
  }

  return (
    <div className={styles.SelfHostedRunners} {...hpcAttrs()} {...testIdProps('self-hosted-runners')}>
      {sharedRunners.length > 0 && (
        <RunnerList
          heading="Shared with this repository"
          listTitle="Shared runner list"
          numberOfRunners={sharedRunners.length}
          {...testIdProps('shared-runners')}
        >
          {sharedRunners.map(runner => {
            return (
              <RunnerListItem
                key={runner.name}
                name={runner.name}
                os={runner.os}
                description={runner.description}
                labels={runner.labels}
                source={runner.source}
                runnerType="shared_runner"
              />
            )
          })}
        </RunnerList>
      )}
      {repoSelfHostedRunnersCount > 0 && (
        <RunnerList
          heading="Repository runners"
          listTitle="Repository self-hosted runner list"
          numberOfRunners={repoSelfHostedRunnersCount}
          {...testIdProps('repository-self-hosted-runners')}
        >
          {repoSelfHostedRunners.map(runner => {
            return (
              <RunnerListItem
                key={runner.name}
                name={runner.name}
                os={runner.os}
                description={runner.description}
                labels={runner.labels}
                source={runner.source}
                runnerType="repo_self_hosted"
              />
            )
          })}
        </RunnerList>
      )}
      {scaleSets.length > 0 && (
        <RunnerList
          heading="Runner scale sets"
          listTitle="Runner scale set list"
          numberOfRunners={scaleSets.length}
          {...testIdProps('repo-scale-sets')}
        >
          {scaleSets.map(runner => {
            return (
              <RunnerListItem
                key={runner.name}
                name={runner.name}
                os={runner.os}
                description={runner.description}
                labels={runner.labels}
                source={runner.source}
                runnerType="repo_scale_set"
              />
            )
          })}
        </RunnerList>
      )}
    </div>
  )
}
